/**
 * Cell shading picker — ONE component for both entry points (Tables toolbox
 * popover and the right-click menu's Cell shading… sub-group). No fill, a
 * preset grid (the shipped neutral ramp + accent, plus a Word-like set),
 * Custom… (the kit ColorPicker, applied live), a per-user Recent row, and a
 * Tint slider bound to the cell's existing `bgOpacity`.
 */
import React from 'react';
import type { Editor } from '@tiptap/react';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ColorPicker } from '../components/ui/color-picker';
import { Icon } from '../components/ui/Icon';
import { usePref } from '../lib/usePref';
import { CELL_SHADES, blendToWhite } from './tables';
import {
  commitShadeDrag,
  selectedCellShade,
  selectedCellTint,
  setCellShade,
  setCellTint,
  snapshotShade,
  type ShadeSnapshot,
} from './tableCommands';

/** Word-like fills — DOCUMENT data (what the .docx cell shading prints as),
 *  the same class of value as CELL_SHADES, not UI chrome. */
const WORD_SHADES: ReadonlyArray<{ hex: string; label: string }> = [
  { hex: 'FFF3BF', label: 'Pale yellow' },
  { hex: 'D3F9D8', label: 'Pale green' },
  { hex: 'D0EBFF', label: 'Pale blue' },
  { hex: 'FFDEEB', label: 'Pale pink' },
  { hex: 'FFE8CC', label: 'Pale orange' },
  { hex: 'E5DBFF', label: 'Pale lavender' },
  { hex: 'FFD43B', label: 'Yellow' },
  { hex: '69DB7C', label: 'Green' },
  { hex: '74C0FC', label: 'Blue' },
  { hex: 'F783AC', label: 'Pink' },
  { hex: 'FFA94D', label: 'Orange' },
  { hex: 'B197FC', label: 'Lavender' },
];

const PRESETS: ReadonlyArray<{ hex: string; label: string }> = [
  ...CELL_SHADES.filter((s): s is { hex: string; label: string } => !!s.hex),
  ...WORD_SHADES.filter((w) => !CELL_SHADES.some((s) => s.hex === w.hex)),
];

const RECENT_MAX = 8;

interface ShadePickerProps {
  editor: Editor;
  disabled: boolean;
  /** Radio role for the swatches: toolbox popover vs. menu. */
  itemRole: 'radio' | 'menuitemradio';
  /** Re-focus the document after a gesture (each entry point's own rule). */
  afterApply: () => void;
}

const norm = (hex: string) => hex.replace('#', '').slice(0, 6).toUpperCase();

export function ShadePicker({ editor, disabled, itemRole, afterApply }: ShadePickerProps) {
  const shade = selectedCellShade(editor.state);
  const tint = selectedCellTint(editor.state);
  const [recent, setRecent] = usePref<string[]>(
    'tables.recentShades',
    'sf.tables.recentShades',
    []
  );
  const [custom, setCustom] = React.useState(false);
  const [draft, setDraft] = React.useState<string | null>(null);

  // Recent remembers a custom colour once the hand settles, not every drag step.
  React.useEffect(() => {
    if (!draft) return;
    const t = setTimeout(() => {
      setRecent((r) => [draft, ...r.filter((x) => x !== draft)].slice(0, RECENT_MAX));
      setDraft(null);
    }, 600);
    return () => clearTimeout(t);
  }, [draft, setRecent]);

  // Custom drag: live applies skip undo history; pointer-up commits ONE step.
  const dragging = React.useRef(false);
  const snap = React.useRef<ShadeSnapshot | null>(null);
  const last = React.useRef<string | null>(null);
  const tintRef = React.useRef(tint);
  tintRef.current = tint;

  const finishDrag = React.useCallback(() => {
    dragging.current = false;
    if (snap.current && last.current) {
      commitShadeDrag(editor, snap.current, last.current, tintRef.current);
      setDraft(last.current);
    }
    snap.current = null;
    last.current = null;
  }, [editor]);

  React.useEffect(() => {
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);
    return () => {
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
      finishDrag();
    };
  }, [finishDrag]);

  const apply = (hex: string | null) => {
    setCellShade(editor, hex, tint);
    afterApply();
  };

  const swatch = (hex: string, label: string) => {
    const on = shade === hex;
    return (
      <button
        key={`${label}-${hex}`}
        type="button"
        role={itemRole}
        aria-checked={on}
        aria-label={label}
        title={label}
        className={`dsg-shade-btn${on ? ' is-on' : ''}`}
        onClick={() => apply(hex)}
        disabled={disabled}
      >
        <span
          className="dsg-shade-chip"
          style={{ background: blendToWhite(hex, 100) }}
          aria-hidden="true"
        >
          {on && <Icon icon={Check} em={0.9} aria-hidden="true" />}
        </span>
      </button>
    );
  };

  return (
    <div className="dsg-shade-picker">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="dsg-shade-none"
        disabled={disabled}
        aria-pressed={!shade}
        onClick={() => apply(null)}
      >
        <span className="dsg-shade-btn is-none dsg-shade-nochip" aria-hidden="true">
          <span className="dsg-shade-chip" />
        </span>
        No fill
      </Button>
      <div className="dsg-shade-grid is-wide" role="group" aria-label="Preset fills">
        {PRESETS.map((s) => swatch(s.hex, s.label))}
      </div>
      {recent.length > 0 && (
        <>
          <div className="dsg-tbl-pop-label">Recent</div>
          <div className="dsg-shade-grid is-wide" role="group" aria-label="Recent fills">
            {recent.map((hex) => swatch(hex, `#${hex}`))}
          </div>
        </>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        aria-expanded={custom}
        onClick={() => setCustom((v) => !v)}
      >
        Custom…
      </Button>
      {custom && (
        <div onPointerDownCapture={() => (dragging.current = true)}>
          <ColorPicker
            value={`#${shade || 'FFFFFF'}`}
            allowAlpha={false}
            onChange={(hex) => {
              const h = norm(hex);
              if (!h || h === shade) return;
              if (dragging.current) {
                if (!snap.current) snap.current = snapshotShade(editor);
                last.current = h;
                setCellShade(editor, h, tint, false);
              } else {
                setCellShade(editor, h, tint);
                setDraft(h);
              }
            }}
          />
        </div>
      )}
      <label className="dsg-shade-tint">
        <span className="dsg-tbl-pop-label">Tint {tint}%</span>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={tint}
          disabled={disabled || !shade}
          aria-label="Fill tint"
          onChange={(e) => setCellTint(editor, Number(e.target.value))}
          onPointerUp={afterApply}
          onKeyUp={afterApply}
        />
      </label>
    </div>
  );
}
